import React from "react";
import { Button, Paper, Typography, Chip, Box } from "@mui/material";
import { formatShortcutDisplay } from "../../../util/src/blueprint/shortcut-keys.js";
import type { ControlRoomCard as CardType } from "../../../util/src/control-room/protocol.js";
import dico from "../../../util/src/language/dico.js";

interface ControlRoomCardProps {
  card: CardType;
  onTrigger: (combo: string) => void;
  disabled?: boolean;
}

const ControlRoomCardComponent = ({
  card,
  onTrigger,
  disabled,
}: ControlRoomCardProps) => {
  const d = (dico() as Record<string, unknown>).controlRoom as Record<
    string,
    string
  >;
  const targetEntries = Object.entries(card.targets);

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        borderRadius: 2,
        minHeight: 120,
        justifyContent: "space-between",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 6 },
      }}
    >
      <Box>
        <Typography variant="subtitle2" fontWeight={700} noWrap>
          {card.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {card.description}
        </Typography>
        <Chip
          label={formatShortcutDisplay(card.combo)}
          size="small"
          sx={{
            fontFamily: "monospace",
            fontWeight: 600,
            fontSize: 11,
            mb: 1,
          }}
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
          {targetEntries.map(([label, count]) => (
            <Chip
              key={label}
              label={count > 1 ? `${label} (${count})` : label}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10 }}
            />
          ))}
        </Box>
      </Box>

      <Button
        variant="contained"
        size="small"
        disabled={disabled}
        onClick={() => onTrigger(card.combo)}
        sx={{ textTransform: "none", mt: 1 }}
      >
        {d.triggerButton}
      </Button>
    </Paper>
  );
};

export const ControlRoomCardUI = React.memo(ControlRoomCardComponent);
